import { RESULT_STATUS, getErrorResult } from "./result";
import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { HelperEvaluator } from "./helper";
import { isValidDate } from "Validation/validator";
import { dateStrToClientDate } from "Util/clientDate";

export class DateEvaluator extends HelperEvaluator {
    constructor(formulaEvaluator, storeContext) {
        super(formulaEvaluator, storeContext);
    }

    evaluateDateConstant(exp) {
        return { Value: exp.DateConstantValue };
    }

    evaluateDateDataReference($app, exp) {
        exp.usingDefault = false;

        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            if (exp.PublicDataFieldDefinitionId) {
                const valStr = this.getters[PROJECT_TYPES.getters.dataFieldValueFromId](
                    exp.PublicDataFieldDefinitionId
                );
                const dfd = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                    exp.PublicDataFieldDefinitionId
                );

                if (valStr) {
                    if (!isValidDate($app, valStr)) {
                        exp.inError = true;
                        exp.errorMessage = `Value of data field ${dfd.Name} is not a valid date.`;
                        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
                    }

                    return { Value: dateStrToClientDate($app, valStr) };
                } else if (exp.DateConstantValue) {
                    exp.usingDefault = true;
                    return { Value: exp.DateConstantValue };
                }
                exp.inError = true;
                exp.errorMessage = `No value for datafield ${dfd.Name}.`;
                return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
            }
            exp.inError = true;
            exp.errorMessage = "No datafield reference specified.";
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    async evaluateDateRelational(exp) {
        const left = await this.evalDate(exp.Children[0]);
        const right = await this.evalDate(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }

        const isStrictCompare = exp.PropertyDictionary.strictcompare === "true";
        const isBefore = exp.PropertyDictionary.beforeafter === "before";

        const value = isBefore
            ? left.Value.isBefore(right.Value, isStrictCompare)
            : left.Value.isAfter(right.Value, isStrictCompare);

        return { Value: value };
    }

    async evaluateDateArithmetic(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalDate(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }

        const isBefore = exp.PropertyDictionary.beforeafter === "before";
        const timeUnits = exp.PropertyDictionary.timeunits;

        // Need this hack because IE doesn't support Math.trunc -_-
        // Don't want to allow fractional days/years here
        const intNumUnits = left.Value < 0 ? Math.ceil(left.Value) : Math.floor(left.Value);
        const numUnits = isBefore ? intNumUnits * -1 : intNumUnits;

        let val;
        switch (timeUnits) {
            case "years":
                val = right.Value.addYears(numUnits);
                break;
            case "months":
                val = right.Value.addMonths(numUnits);
                break;
            case "business":
                val = this.getters[PROGRAM_TYPES.getters.getNthBusinessDay](right.Value, numUnits);
                break;
            case "calendar":
                val = right.Value.addDays(numUnits);
                break;
            default:
                throw `evaluateDateArithmetic: invalid value ${timeUnits} for property timeunits`;
        }

        if (val.Year < 1) {
            exp.inError = true;
            exp.errorMessage = "Cannot evaluate expression with date earlier than 1/1/0001";
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        return { Value: val };
    }

    async evaluateConditionalDate(exp) {
        const condition = await this.evalBool(exp.Children[0]);
        const left = await this.evalDate(exp.Children[1]);
        const right = await this.evalDate(exp.Children[2]);

        const error = getErrorResult([condition, left, right]);
        if (error) {
            return error;
        }

        const value = condition.Value ? left.Value : right.Value;

        return { Value: value };
    }
}
